'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { createRotationAudio } from '@/lib/rotation/audio';
import { initialShelf, changeShelf, shelfTransitionDuration } from '@/lib/rotation/filter';
import { RANGE_LABELS, type RotationRange } from '@/lib/rotation/types';
import { ArrowLeft, VolumeX, Volume2 } from 'lucide-react';
import { motionValue } from 'motion';
import type { RotationAlbum, RotationData } from '@/lib/rotation/types';
import { activeEase, tween, type SleeveMotion } from '@/lib/rotation/motion';
import { fetchAlbumDetails } from '@/lib/rotation/client';
import { useMountEffect } from '@/hooks/useMountEffect';
import { AlbumSelection, type Selection } from '@/components/rotation/AlbumSelection';
import { ShelfFallback } from '@/components/rotation/ShelfFallback';
import { FlipCursor } from '@/components/rotation/FlipCursor';
import { RotationRanges } from '@/components/rotation/RotationRanges';
const ShelfScene = dynamic(() => import('@/components/rotation/ShelfScene'), {
  ssr: false
});
export default function RotationPageClient(t0) {
  const $ = _c(98);
  const {
    initialData
  } = t0;
  const [range, setRange] = useState("short_term");
  let t1;
  if ($[0] !== initialData.short_term.albums) {
    t1 = () => initialShelf(initialData.short_term.albums);
    $[0] = initialData.short_term.albums;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [items, setItems] = useState(t1);
  const [revision, setRevision] = useState(0);
  const [ready, setReady] = useState(false);
  const [audio] = useState(createRotationAudio);
  const [soundOn, setSoundOn] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);
  const filterTimer = useRef(undefined);
  const albums = initialData[range].albums;
  const [selection, setSelection] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const detailsRequest = useRef(0);
  const [focus] = useState(_temp);
  const activeModel = useRef(null);
  const closeTimer = useRef(undefined);
  let t2;
  if ($[2] !== audio || $[3] !== focus) {
    t2 = () => () => {
      clearTimeout(closeTimer.current);
      focus.stop();
      clearTimeout(filterTimer.current);
      audio.dispose();
    };
    $[2] = audio;
    $[3] = focus;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useMountEffect(t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = id => {
      const request = detailsRequest.current = detailsRequest.current + 1;
      fetchAlbumDetails(id).then(result => {
        if (request === detailsRequest.current) {
          setSelection(current => current?.album.id === id ? {
            ...current,
            ...result
          } : current);
        }
      });
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const loadDetails = t3;
  let t4;
  if ($[6] !== audio || $[7] !== focus || $[8] !== selection) {
    t4 = (album, model, t5) => {
      const back = t5 === undefined ? false : t5;
      if (selection) {
        return;
      }
      audio.cancelThud();
      audio.play("whoosh");
      activeModel.current = model;
      model.open();
      if (back) {
        model.flip(true);
      }
      tween(focus, 1, 700, activeEase);
      setSelection({
        album,
        back,
        closing: false,
        details: null,
        error: null
      });
      loadDetails(album.id);
      requestAnimationFrame(_temp2);
      setAnnouncement(`${album.title} by ${album.artist}. ${back ? "Back cover. Track list." : "Front cover."}`);
    };
    $[6] = audio;
    $[7] = focus;
    $[8] = selection;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const open = t4;
  let t5;
  if ($[10] !== selection) {
    t5 = () => {
      if (!selection || selection.closing) {
        return;
      }
      const back_0 = !selection.back;
      activeModel.current?.flip(back_0);
      setSelection({
        ...selection,
        back: back_0
      });
      setAnnouncement(`${selection.album.title}. ${back_0 ? "Back cover. Track list." : "Front cover."}`);
    };
    $[10] = selection;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const flip = t5;
  let t6;
  if ($[12] !== audio || $[13] !== focus || $[14] !== selection) {
    t6 = () => {
      if (!selection || selection.closing) {
        return;
      }
      detailsRequest.current = detailsRequest.current + 1;
      clearTimeout(closeTimer.current);
      audio.cancelThud();
      audio.play("whoosh");
      audio.play("thud", 1.098);
      activeModel.current?.close();
      tween(focus, 0, 700, activeEase);
      setSelection({
        ...selection,
        closing: true
      });
      setAnnouncement(`${selection.album.title} returned to the shelf.`);
      closeTimer.current = setTimeout(() => {
        setSelection(null);
        activeModel.current = null;
        requestAnimationFrame(() => {
          const original = document.querySelector(`[data-album-id="${selection.album.id}"][data-present="true"] .rotation-hit`);
          const fallback = document.querySelector("[data-present=\"true\"][data-settled=\"true\"] .rotation-hit");
          (original ?? fallback)?.focus({
            preventScroll: true
          });
        });
      }, 1340);
    };
    $[12] = audio;
    $[13] = focus;
    $[14] = selection;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const close = t6;
  let t7;
  if ($[16] !== selection) {
    t7 = () => {
      if (selection) {
        setSelection({
          ...selection,
          error: null
        });
        loadDetails(selection.album.id);
      }
    };
    $[16] = selection;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const retry = t7;
  let t8;
  if ($[18] !== audio || $[19] !== initialData || $[20] !== items || $[21] !== range || $[22] !== selection) {
    t8 = next => {
      if (next === range || selection) {
        return;
      }
      clearTimeout(filterTimer.current);
      audio.cancelThud();
      audio.play("whoosh");
      const stagger = window.innerWidth < 900 && window.innerHeight > window.innerWidth ? 161 : 75;
      const nextItems = changeShelf(items, initialData[next].albums, stagger, stagger === 161 ? 6 : 9);
      setItems(nextItems);
      setRange(next);
      setRevision(_temp3);
      setAnnouncement(`${RANGE_LABELS[next]}. ${initialData[next].albums.length} albums on the shelf.`);
      filterTimer.current = setTimeout(() => setItems(_temp5), shelfTransitionDuration(nextItems));
    };
    $[18] = audio;
    $[19] = initialData;
    $[20] = items;
    $[21] = range;
    $[22] = selection;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const chooseRange = t8;
  let t9;
  if ($[24] !== audio) {
    t9 = async () => {
      setAudioBusy(true);
      const enabled = await audio.toggle();
      setSoundOn(enabled);
      setAudioBusy(false);
      setAnnouncement(enabled ? "Sound on." : "Sound off.");
    };
    $[24] = audio;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const toggleSound = t9;
  let t10;
  if ($[26] !== close || $[27] !== flip || $[28] !== selection) {
    t10 = event => {
      if (!selection) {
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      } else {
        if (event.code === "Space" && !(event.target instanceof HTMLButtonElement) && !(event.target instanceof HTMLAnchorElement)) {
          event.preventDefault();
          flip();
        }
      }
    };
    $[26] = close;
    $[27] = flip;
    $[28] = selection;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const onKey = useEffectEvent(t10);
  let t11;
  if ($[30] !== onKey) {
    t11 = () => {
      const listener = event_0 => onKey(event_0);
      window.addEventListener("keydown", listener);
      return () => window.removeEventListener("keydown", listener);
    };
    $[30] = onKey;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [];
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  useEffect(t11, t12);
  const t13 = selection?.album.id ?? null;
  const t14 = selection?.closing ?? false;
  const t15 = selection?.details ?? null;
  const t16 = selection?.error ?? null;
  let t17;
  if ($[33] !== audio) {
    t17 = () => audio.play("focus");
    $[33] = audio;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== flip || $[36] !== open || $[37] !== retry || $[38] !== t13 || $[39] !== t14 || $[40] !== t15 || $[41] !== t16 || $[42] !== t17) {
    t18 = {
      selectedId: t13,
      closing: t14,
      details: t15,
      error: t16,
      onOpen: open,
      onFlip: flip,
      onRetry: retry,
      onHover: t17
    };
    $[35] = flip;
    $[36] = open;
    $[37] = retry;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const interaction = t18;
  const t19 = !!selection;
  const t20 = selection?.closing ?? false;
  let t21;
  if ($[44] !== close || $[45] !== selection) {
    t21 = e => {
      if (selection && e.target instanceof HTMLCanvasElement) {
        close();
      }
    };
    $[44] = close;
    $[45] = selection;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== albums) {
    t22 = albums.map(_temp6);
    $[47] = albums;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Link href="/craft" aria-label="Back to craft"><ArrowLeft size={15} /></Link>;
    t24 = <span>in rotation</span>;
    $[49] = t23;
    $[50] = t24;
  } else {
    t23 = $[49];
    t24 = $[50];
  }
  const t25 = soundOn ? "Mute sound" : "Enable sound";
  let t26;
  if ($[51] !== soundOn) {
    t26 = soundOn ? <Volume2 size={18} /> : <VolumeX size={18} />;
    $[51] = soundOn;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== audioBusy || $[54] !== soundOn || $[55] !== t25 || $[56] !== t26 || $[57] !== toggleSound) {
    t27 = <header className="rotation-header">{t23}{t24}<button className="rotation-sound" aria-label={t25} aria-pressed={soundOn} disabled={audioBusy} onClick={toggleSound}>{t26}</button></header>;
    $[53] = audioBusy;
    $[54] = soundOn;
    $[55] = t25;
    $[56] = t26;
    $[57] = toggleSound;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <p className="rotation-intro">My most-played albums.</p>;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== albums) {
    t29 = <noscript><ShelfFallback albums={albums} /></noscript>;
    $[60] = albums;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  const t30 = revision === 0;
  let t31;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setReady(true);
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== focus || $[64] !== interaction || $[65] !== items || $[66] !== t30) {
    t32 = <div className="rotation-canvas"><ShelfScene items={items} interaction={interaction} focus={focus} intro={t30} onReady={t31} /></div>;
    $[63] = focus;
    $[64] = interaction;
    $[65] = items;
    $[66] = t30;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== selection) {
    t33 = selection && <FlipCursor />;
    $[68] = selection;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== close || $[71] !== flip || $[72] !== selection) {
    t34 = selection && <AlbumSelection selection={selection} onClose={close} onFlip={flip} />;
    $[70] = close;
    $[71] = flip;
    $[72] = selection;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== initialData || $[75] !== range) {
    t35 = initialData[range].error && <p className="rotation-empty">{initialData[range].error}</p>;
    $[74] = initialData;
    $[75] = range;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  const t36 = !!selection;
  let t37;
  if ($[77] !== chooseRange || $[78] !== range || $[79] !== t36) {
    t37 = <RotationRanges range={range} disabled={t36} onChange={chooseRange} />;
    $[77] = chooseRange;
    $[78] = range;
    $[79] = t36;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== announcement) {
    t38 = <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{announcement}</p>;
    $[81] = announcement;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== range || $[84] !== ready || $[85] !== t19 || $[86] !== t20 || $[87] !== t21 || $[88] !== t22 || $[89] !== t27 || $[90] !== t29 || $[91] !== t32 || $[92] !== t33 || $[93] !== t34 || $[94] !== t35 || $[95] !== t37 || $[96] !== t38) {
    t39 = <main className="rotation-page" data-active={t19} data-range={range} data-closing={t20} data-ready={ready} onPointerDown={t21}>{t22}{t27}{t28}{t29}{t32}{t33}{t34}{t35}{t37}{t38}</main>;
    $[83] = range;
    $[84] = ready;
    $[85] = t19;
    $[86] = t20;
    $[87] = t21;
    $[88] = t22;
    $[89] = t27;
    $[90] = t29;
    $[91] = t32;
    $[92] = t33;
    $[93] = t34;
    $[94] = t35;
    $[95] = t37;
    $[96] = t38;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  return t39;
}
function _temp6(a) {
  return <link key={a.id} rel="preload" as="image" href={a.coverUrl} />;
}
function _temp5(current_0) {
  return current_0.filter(_temp4);
}
function _temp4(item) {
  return item.present;
}
function _temp3(value) {
  return value + 1;
}
function _temp2() {
  return document.querySelector("[data-selected=\"true\"] .rotation-hit")?.focus({
    preventScroll: true
  });
}
function _temp() {
  return motionValue(0);
}